"use client";
import "@/components/home/blog.css";
import "@/components/ui/atoms/categories/data-display.css";
import "@/components/ui/atoms/categories/layout.css";
import "@/components/ui/molecules/categories/page-chrome.css";

import { type Author, AuthorRow } from "@/components/home/author-row";
import { type PostCategory, PostMeta } from "@/components/home/post-meta";
import { SiteFooter } from "@/components/home/site-footer";

import { SiteNav } from "@/components/home/site-nav";
import { Button } from "@/components/ui/shadcn/button";
import { Input } from "@/components/ui/shadcn/input";
import { heroSerif } from "@/lib/theme/hero-serif";
import Image from "next/image";
import Link from "next/link";

import { memo } from "react";

const KANE: Author = { name: "Kane", src: "/graphics/dw-founder-kane2.webp" };
const DYLAN: Author = { name: "Dylan", src: "/graphics/dw-founder-dylan.webp" };

type Post = {
    slug: string;
    title: string;
    description?: string;
    dateISO: string;
    dateLabel: string;
    category: PostCategory;
    authors: Author[];
    read: string;
    href?: string;
    image?: { src: string; alt: string };
};

// Post bodies aren't written yet — every href points at /blog until the
// slug routes exist.
const FEATURED: Post = {
    slug: "four-minute-clerk",
    title: "The four-minute clerk",
    description: "Every dispute in the United States goes through the same machine. What happens to a letter after you mail it, and why most were never going to work.",
    dateISO: "2026-07-05T12:00:00.000Z",
    dateLabel: "Jul 5, 2026",
    category: "research",
    authors: [KANE, DYLAN],
    read: "9 min read",
    href: "/blog/four-minute-clerk",
    image: { src: "/graphics/dw-burn-bg.webp", alt: "A prismatic beam burning across a deep blue field" },
};

const SIDE_POSTS: Post[] = [
    {
        slug: "fluid-grows",
        title: "Fluid grows with each round",
        description: "Fluid records predicted against realized score gains and recalibrates for your business every round.",
        dateISO: "2026-07-03T12:00:00.000Z",
        dateLabel: "Jul 3, 2026",
        category: "product",
        authors: [KANE],
        read: "5 min read",
        image: { src: "/graphics/dw-flower-spectrum.webp", alt: "A luminous flower rendered in full-spectrum light" },
    },
    {
        slug: "computed-metro-2",
        title: "Why we computed Metro 2 instead of templating it",
        description: "48 field-level rules from the 2023 CRRG, run against every account. A violation is a data structure, not a sentence.",
        dateISO: "2026-06-28T12:00:00.000Z",
        dateLabel: "Jun 28, 2026",
        category: "research",
        authors: [DYLAN],
        read: "7 min read",
        image: { src: "/graphics/dw-iso-bg.webp", alt: "Luminous towers rising out of a prismatic beam" },
    },
];

const DIRECTORY: Post[] = [
    FEATURED,
    SIDE_POSTS[0],
    SIDE_POSTS[1],
    {
        slug: "round-two",
        title: "Round two is a different legal instrument",
        dateISO: "2026-06-24T12:00:00.000Z",
        dateLabel: "Jun 24, 2026",
        category: "research",
        authors: [KANE],
        read: "6m",
    },
    {
        slug: "white-label-included",
        title: "White label without the five-figure invoice",
        dateISO: "2026-06-12T12:00:00.000Z",
        dateLabel: "Jun 12, 2026",
        category: "product",
        authors: [DYLAN],
        read: "3m",
    },
    {
        slug: "approval-gate",
        title: "The approval gate: why nothing ships without you",
        dateISO: "2026-06-06T12:00:00.000Z",
        dateLabel: "Jun 6, 2026",
        category: "product",
        authors: [KANE],
        read: "4m",
    },
    {
        slug: "introducing-disputewell",
        title: "Introducing DisputeWell",
        dateISO: "2026-06-01T12:00:00.000Z",
        dateLabel: "Jun 1, 2026",
        category: "company",
        authors: [KANE, DYLAN],
        read: "3m",
    },
];

const CATEGORIES = ["all", "product", "research", "company"] as const;

const GUIDES = [
    { slug: "croa-window", title: "The CROA cancellation window, computed", dateLabel: "Jun 30, 2026", glyph: "§" },
    { slug: "metro-2-fields", title: "Reading a Metro 2 field table", dateLabel: "Jun 22, 2026", glyph: "M2" },
    { slug: "reinsertion", title: "Reinsertion and §611(a)(5)(B)", dateLabel: "Jun 15, 2026", glyph: "611" },
    { slug: "thirty-day-clock", title: "The FCRA 30-day clock, round by round", dateLabel: "Jun 8, 2026", glyph: "30d" },
] as const;

const CHANGELOG = [
    { slug: "fluid-playground", title: "Fluid Playground", dateLabel: "Jul 5, 2026" },
    { slug: "fluid-popout", title: "Fluid popout and the ChatKit assistant", dateLabel: "Jul 5, 2026" },
    { slug: "org-timezone", title: "One business timezone for your whole org", dateLabel: "Jul 4, 2026" },
    { slug: "editorial-site", title: "A new front page and features page", dateLabel: "Jul 5, 2026" },
] as const;

const PostCard = memo(
    function PostCard({ post, featured = false }: { post: Post; featured?: boolean }) {
        return (
            <article className="dw-blog__card" data-component="PostCard" data-location="src/components/home/blog-content.tsx">
                <Link href={post.href ?? "/blog"} className="dw-blog__card-link dw-card">
                    {post.image && (
                        <div className="dw-blog__card-media">
                            <Image
                                src={post.image.src}
                                alt={post.image.alt}
                                fill
                                sizes={featured ? "(min-width: 1024px) 66vw, 100vw" : "(min-width: 1024px) 33vw, 100vw"}
                                className="dw-blog__card-img"
                            />
                        </div>
                    )}
                    <div className="dw-blog__card-body">
                        <PostMeta dateISO={post.dateISO} dateLabel={post.dateLabel} category={post.category} />
                        {featured
                            ? <h3 className="dw-text-marketing-display" data-tone="foreground">{post.title}</h3>
                            : <h3 className="dw-text-lead" data-tone="foreground">{post.title}</h3>}
                        {post.description && <p className="dw-text-body" data-tone="muted">{post.description}</p>}
                        <AuthorRow authors={post.authors} read={post.read} />
                    </div>
                </Link>
            </article>
        );
    }
);

export const BlogContent = memo(function BlogContent() {

    return (
        <div className={heroSerif.variable} data-component="BlogContent" data-location="src/components/home/blog-content.tsx">
            <SiteNav />

            <main id="main-content" className="dw-blog__main dw-home__measure">
                <h1 className="dw-sr-only">Blog</h1>

                {/* Featured spread: hero post left, two stacked right. */}
                <section className="dw-blog__featured" aria-label="Featured posts">
                    <div className="dw-blog__featured-hero">
                        <PostCard post={FEATURED} featured />
                    </div>
                    <div className="dw-blog__featured-side">
                        {SIDE_POSTS.map((post) => (
                            <PostCard key={post.slug} post={post} />
                        ))}
                    </div>
                </section>

                {/* Newsletter strip. */}
                <section className="dw-blog__newsletter" aria-label="Newsletter">
                    <p className="dw-text-body" data-tone="foreground">Subscribe to the newsletter for new posts and product updates.</p>
                    <form className="dw-blog__newsletter-form" onSubmit={(event) => event.preventDefault()}>
                        <Input type="email" inputMode="email" autoComplete="email" required placeholder="Email address" aria-label="Email address" />
                        <Button variant="bare" size="auto" type="submit" className="dw-blog__newsletter-btn dw-text-body dw-tabular-nums" data-tone="foreground">
                            Subscribe →
                        </Button>
                    </form>
                </section>

                {/* Directory: category nav + row list. */}
                <section className="dw-blog__directory-section" aria-label="All posts">
                    <div className="dw-toolbar" data-justify="between" data-gap="lg">
                        <nav className="dw-blog__cats" aria-label="Blog categories">
                            {CATEGORIES.map((category, index) => (
                                <Link key={category} href="/blog" className="dw-blog__cat dw-text-caption dw-tabular-nums" data-tone={index === 0 ? "foreground" : "muted"} aria-current={index === 0 ? "page" : undefined}>
                                    {category}
                                </Link>
                            ))}
                        </nav>
                        <Input type="search" placeholder="Search" aria-label="Search blog posts" className="dw-blog__search" />
                    </div>

                    <div className="dw-blog__directory dw-card dw-list" data-divided="block">
                        {DIRECTORY.map((post) => (
                            <Link key={post.slug} href={post.href ?? "/blog"} className="dw-list__item" data-interactive="true">
                                <div className="dw-blog__row">
                                    <PostMeta dateISO={post.dateISO} dateLabel={post.dateLabel} category={post.category} />
                                    <p className="dw-text-body" data-tone="foreground">{post.title}</p>
                                    <p className="dw-text-body" data-tone="muted">{post.authors.map((author) => author.name).join(", ")}</p>
                                    <p className="dw-text-caption" data-tone="muted">{post.read.replace(" min read", "m")}</p>
                                </div>
                            </Link>
                        ))}
                        <div className="dw-blog__more-wrap">
                            <Button variant="bare" size="auto" type="button" className="dw-blog__more dw-text-body dw-tabular-nums" data-tone="foreground">
                                View more ↓
                            </Button>
                        </div>
                    </div>
                </section>

                {/* Pull quote. */}
                <section aria-label="Pull quote">
                    <figure className="dw-blog__quote">
                        <blockquote>
                            <p className="dw-text-marketing-title" data-tone="foreground">
                                &ldquo;A dispute succeeds when its claim survives code compression intact and lands in a form the furnisher&apos;s
                                automation cannot resolve against its own record.&rdquo;
                            </p>
                        </blockquote>
                        <figcaption className="dw-blog__quote-caption dw-text-body" data-tone="muted">
                            From &ldquo;The four-minute clerk&rdquo;
                            <Link href="/blog" data-tone="primary">
                                Read more →
                            </Link>
                        </figcaption>
                    </figure>
                </section>

                {/* Guides. */}
                <section className="dw-blog__strip" aria-label="Guides">
                    <h2 className="dw-text-heading" data-tone="foreground">Guides</h2>
                    <div className="dw-blog__strip-grid">
                        {GUIDES.map((guide) => (
                            <Link key={guide.slug} href="/blog" className="dw-blog__guide">
                                <div className="dw-blog__guide-copy">
                                    <p className="dw-text-body" data-tone="foreground">{guide.title}</p>
                                    <p className="dw-text-caption dw-tabular-nums" data-tone="muted">{guide.dateLabel}</p>
                                </div>
                                <div className="dw-blog__guide-well dw-panel" data-variant="subtle" aria-hidden>
                                    <span className="dw-text-kpi" data-tone="primary">{guide.glyph}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Changelog. */}
                <section className="dw-blog__strip" aria-label="Changelog">
                    <h2 className="dw-text-heading" data-tone="foreground">Changelog</h2>
                    <div className="dw-blog__strip-grid">
                        {CHANGELOG.map((entry) => (
                            <Link key={entry.slug} href="/blog" className="dw-blog__log">
                                <p className="dw-text-caption dw-tabular-nums" data-tone="muted">{entry.dateLabel}</p>
                                <p className="dw-text-body" data-tone="foreground">{entry.title}</p>
                            </Link>
                        ))}
                    </div>
                    <Link href="/features" className="dw-blog__strip-more dw-text-body dw-tabular-nums" data-tone="primary">
                        See what&apos;s new in DisputeWell →
                    </Link>
                </section>

            </main>

            <SiteFooter />
        </div>
    );
});
